import React, { useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Badge, Button, Card, CardHeader } from "reactstrap";
import CustomSearchInput from "../../../../components/common/CustomSearchInput";
import CustomTooltipWrapper from "../../../../components/common/CustomTooltipWrapper";
import DeleteModal from "../../../../components/common/DeleteModal";
import Loader from "../../../../components/common/Loader";
import NoRecords from "../../../../components/common/NoRecords";
import AddReleaseNoteModal from "../../../../components/pages/settings/AddReleaseNoteModal";
import { addReleaseNote, deleteReleaseNote, getReleaseNotes, updateReleaseNote } from "../../../../store/release-notes/actions";
import { DEFAULT_ROW_PER_PAGE } from "../../../../helpers/constants";

const ReleaseNotes = (props) => {
  const dispatch = useDispatch();
  const { data: releaseNotesData, total: totalReleaseNotes } = useSelector((state) => state?.ReleaseNotes);

  const [releaseNotesPayload, setReleaseNotesPayload] = useState({
    search: "",
    page: 1,
    limit: DEFAULT_ROW_PER_PAGE,
    filter: {
      id__icontains: "",
      version__icontains: "",
      title_en__icontains: "",
    },
  });

  const [selectedReleaseNote, setSelectedReleaseNote] = useState(null);
  const [tableLoader, setTableLoader] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [isDeleteActionAllowed, setIsDeleteActionAllowed] = useState(false);

  // add or edit action handlers
  const handleModalOpen = (data) => {
    setSelectedReleaseNote(data);
    setIsModalOpen(true);
  };
  const handleModalClose = () => {
    setSelectedReleaseNote(null);
    setIsModalOpen(false);
  };
  const handleSave = (values, formikValidation) => {
    if (selectedReleaseNote && selectedReleaseNote?.id) {
      setSaveLoader(true);
      dispatch(updateReleaseNote(selectedReleaseNote?.id, values))
        .then((res) => {
          if (res) {
            handleModalClose();
            formikValidation.resetForm();
            handleGetReleaseNotes(releaseNotesPayload);
          }
          setSaveLoader(false);
        })
        .catch(() => {
          setSaveLoader(false);
        });
    } else {
      setSaveLoader(true);
      dispatch(addReleaseNote(values))
        .then((res) => {
          if (res) {
            handleModalClose();
            formikValidation.resetForm();
            handleGetReleaseNotes(releaseNotesPayload);
          }
          setSaveLoader(false);
        })
        .catch(() => {
          setSaveLoader(false);
        });
    }
  };

  // delete action handlers
  const handleDeleteModalOpen = (data) => {
    setSelectedReleaseNote(data);
    setIsDeleteModalOpen(true);
  };
  const handleDeleteModalClose = () => {
    setIsDeleteActionAllowed(false);
    setSelectedReleaseNote(null);
    setIsDeleteModalOpen(false);
  };
  const handleDelete = () => {
    if (selectedReleaseNote && selectedReleaseNote?.id) {
      setDeleteLoader(true);
      dispatch(
        deleteReleaseNote({
          id: selectedReleaseNote?.id,
          payload: { is_admin: isDeleteActionAllowed },
        })
      )
        .then((res) => {
          if (res.data.status === 200) {
            if (!res?.data?.data?.action_allowed) {
              handleDeleteModalClose();
            }
            setIsDeleteActionAllowed(res?.data?.data?.action_allowed);
            toast.warn(res?.data?.detail);
          } else if (res.data.status === 204) {
            toast.success(res?.data?.detail);
            handleGetReleaseNotes(releaseNotesPayload);
            handleDeleteModalClose();
          }
          setDeleteLoader(false);
        })
        .catch((error) => {
          setDeleteLoader(false);
          handleDeleteModalClose();
          console.log(error);
        });
    }
  };

  // handle page change
  const handlePageChange = (value) => {
    setReleaseNotesPayload((prevState) => ({
      ...prevState,
      page: value,
    }));
  };

  // handle rows per page change
  const handleRowsPerPageChange = (value) => {
    setReleaseNotesPayload((prevState) => ({
      ...prevState,
      limit: value,
    }));
  };

  // handle get release notes
  const handleGetReleaseNotes = useCallback(
    (releaseNotesPayload) => {
      setTableLoader(true);
      dispatch(getReleaseNotes(releaseNotesPayload))
        .then((res) => {
          setTableLoader(false);
        })
        .catch((error) => {
          setTableLoader(false);
          console.log(error);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    handleGetReleaseNotes(releaseNotesPayload);
  }, [releaseNotesPayload, handleGetReleaseNotes]);

  const columns = [
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{props.t("common.id")}</div>
          <CustomSearchInput
            columnWise={true}
            columnSearchKey="id__icontains"
            className="column-search-input"
            payload={releaseNotesPayload}
            setPayload={setReleaseNotesPayload}
          />
        </div>
      ),
      selector: (row) => row?.id,
      minWidth: "100px",
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{props.t("releaseNotes.version")}</div>
          <CustomSearchInput
            columnWise={true}
            columnSearchKey="version__icontains"
            className="column-search-input"
            payload={releaseNotesPayload}
            setPayload={setReleaseNotesPayload}
          />
        </div>
      ),
      selector: (row) => row?.version,
      minWidth: "120px",
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{props.t("releaseNotes.titleEn")}</div>
          <CustomSearchInput
            columnWise={true}
            columnSearchKey="title_en__icontains"
            className="column-search-input"
            payload={releaseNotesPayload}
            setPayload={setReleaseNotesPayload}
          />
        </div>
      ),
      selector: (row) => row?.title_en,
      minWidth: "200px",
      wrap: true,
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{props.t("releaseNotes.releaseDate")}</div>
        </div>
      ),
      selector: (row) => row?.release_date,
      minWidth: "130px",
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{props.t("releaseNotes.published")}</div>
        </div>
      ),
      selector: (row) => (
        <Badge color={row?.is_published ? "success" : "warning"} className="text-uppercase">
          {row?.is_published ? props.t("common.yes") : props.t("common.no")}
        </Badge>
      ),
      minWidth: "120px",
      center: true,
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{props.t("releaseNotes.features")}</div>
        </div>
      ),
      selector: (row) => row?.features?.length || 0,
      minWidth: "100px",
      center: true,
    },
    {
      name: (
        <div className="table-actions font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{props.t("common.actions")}</div>
        </div>
      ),
      selector: (row) => (
        <div className="table-actions">
          <button
            type="button"
            className="btn btn-icon btn-topbar btn-ghost-primary rounded-circle light-dark-mode shadow-none"
            onClick={() => {
              handleModalOpen(row);
            }}
          >
            <CustomTooltipWrapper target={`tooltip-${row?.id}`} tooltipBody={props.t("common.edit")} />
            <i className="ri-pencil-fill fs-18" id={`tooltip-${row?.id}`} />
          </button>
          <button
            type="button"
            className="btn btn-icon btn-topbar btn-ghost-primary rounded-circle light-dark-mode shadow-none"
            onClick={() => {
              handleDeleteModalOpen(row);
            }}
          >
            <CustomTooltipWrapper target={`tooltip-${row?.id}-delete`} tooltipBody={props.t("common.delete")} />
            <i className=" ri-delete-bin-fill fs-18" id={`tooltip-${row?.id}-delete`} />
          </button>
        </div>
      ),
      minWidth: "150px",
      center: true,
    },
  ];

  document.title = `Veritas | ${props.t("sidebarMenu.releaseNotes")}`;

  return (
    <>
      <div className="page-content layout-main-container">
        <div className="container-header ">
          <CustomSearchInput
            className="custom-search-input"
            payload={releaseNotesPayload}
            setPayload={setReleaseNotesPayload}
          />
          <div className="contain-header-right">
            <Button
              color="success"
              className="btn-load d-flex align-items-center"
              type="submit"
              onClick={handleModalOpen}
            >
              <i className="ri-add-fill fs-18" />
              <span className="flex-grow-1 ms-2">{props.t("releaseNotes.addReleaseNote")}</span>
            </Button>
          </div>
        </div>
        <Card className="container-body">
          {tableLoader ? <Loader /> : null}
          <CardHeader className="table-title">{props.t("sidebarMenu.releaseNotes")}</CardHeader>
          <DataTable
            className="data-table"
            persistTableHead
            columns={columns}
            data={[...releaseNotesData]}
            pagination
            paginationServer
            paginationTotalRows={totalReleaseNotes}
            noDataComponent={<NoRecords />}
            onChangeRowsPerPage={handleRowsPerPageChange}
            onChangePage={handlePageChange}
            paginationPerPage={DEFAULT_ROW_PER_PAGE}
            paginationComponentOptions={{
              rowsPerPageText: props.t("common.rowsPerPage"),
              rangeSeparatorText: props.t("common.rangeSeparator"),
            }}
          />
        </Card>
      </div>
      {/* add / edit modal */}
      <AddReleaseNoteModal
        releaseNote={selectedReleaseNote}
        isModalOpen={isModalOpen}
        onCancel={handleModalClose}
        onSave={handleSave}
        loader={saveLoader}
      />
      {/* delete modal */}
      <DeleteModal
        isModalOpen={isDeleteModalOpen}
        onCloseBtnHandler={handleDeleteModalClose}
        onConfirmBtnHandler={handleDelete}
        title={`${props.t("common.delete")} ${props.t("releaseNotes.releaseNote")}`}
        body={props.t("confirmation.deleteMessage", {
          module: props.t("releaseNotes.releaseNote_small"),
        })}
        actionBtnTitle={isDeleteActionAllowed ? `${props.t("common.confirm")}` : ` ${props.t("common.delete")}`}
        loader={deleteLoader}
      />
    </>
  );
};

export default withTranslation()(ReleaseNotes);
